'use client';

import { css } from '@repo/pandacss/styled-system/css';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { SortByDropdown } from '../../../OffersFilter/SortByDropdown';
import { StickyBar } from '../../../OffersFilter/StickyBar';
import { useFilterEvents } from '../../../OffersFilter/useFilterEvents';
import { useShowStickyBar } from '../../../OffersFilter/useShowStickyBar';
import { getFiltersForPage } from '../filterConfig';
import { SelectedFilterPills } from '../SelectedFilterPills';
import { useOffersFilterStore } from '../store/useOffersFilterStore';
import { BonusSelect } from './BonusSelect';
import { LocationSelect } from './LocationSelect';
import { MinBalanceSelect } from './MinBalanceSelect';
import { MinDepositSelect } from './MinDepositSelect';
import { ProductTypeSelect } from './ProductTypeSelect';
import { TermLengthSelect } from './TermLengthSelect';

export type DesktopFiltersProps = {
  initialLocation?: StateAbbreviation;
  regionCode?: StateAbbreviation;
  mainSectionRef: RefObject<HTMLDivElement | null>;
  slug: string;
  analyticsProps: { actionSection: string; pageIdentifier: string };
  onClearFilters?: () => void;
  initialClientRegion?: string;
  showFilterSkeleton?: boolean;
};

const wrapperClass = css({
  display: { base: 'none', sm: 'flex' },
  flexDirection: 'column',
  columnGap: '2',
  rowGap: '2',
  pt: '4',
});
const filtersRowClass = css({
  display: 'flex',
  flexDirection: { base: 'column', sm: 'row' },
  alignItems: { base: 'center', sm: 'end' },
  flexGrow: '1',
  columnGap: '2',
  rowGap: '2',
});
const skeletonClass = css({
  height: '10',
  width: { base: 'full', sm: '[150px]' },
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  backgroundColor: 'neutral.lighter2',
  animationName: '[pulse]',
  animationDuration: '[1.5s]',
  animationTimingFunction: 'ease-in-out',
  animationIterationCount: 'infinite',
});

export const DesktopFilters = ({
  mainSectionRef,
  slug,
  initialLocation,
  regionCode,
  analyticsProps,
  onClearFilters,
  initialClientRegion,
  showFilterSkeleton,
}: DesktopFiltersProps) => {
  const filterBoxRef = useRef<HTMLDivElement>(null);
  const showStickyBar = useShowStickyBar(filterBoxRef, mainSectionRef);

  const { sortBySelectEvent, clearAllFiltersEvent, removeFilterEvent } = useFilterEvents({
    actionSection: analyticsProps.actionSection,
    pageIdentifier: analyticsProps.pageIdentifier,
  });

  const { sortOptions, switchBarFilters } = useOffersFilterStore(
    ({ sortOptions, switchBarFilters }) => ({ sortOptions, switchBarFilters }),
    shallow,
  );

  useEffect(() => {
    switchBarFilters();
  }, [showStickyBar]);

  const scrollBackToMainBar = useCallback(() => {
    if (filterBoxRef.current) {
      const y = filterBoxRef.current.getBoundingClientRect().top + window.scrollY - 15;
      window.scrollTo({ top: y, behavior: 'instant' });
    }
  }, []);

  const renderFilter = (componentName: string, isStickyBar = false) => {
    switch (componentName) {
      case 'ProductTypeSelect':
        return (
          <ProductTypeSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            regionCode={regionCode}
            {...analyticsProps}
          />
        );
      case 'LocationSelect':
        if (!initialLocation) return null;
        return (
          <LocationSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            {...analyticsProps}
          />
        );
      case 'TermLengthSelect':
        return (
          <TermLengthSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            {...analyticsProps}
          />
        );
      case 'MinDepositSelect':
        return (
          <MinDepositSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            {...analyticsProps}
          />
        );
      case 'MinBalanceSelect':
        return (
          <MinBalanceSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            {...analyticsProps}
          />
        );
      case 'BonusSelect':
        return (
          <BonusSelect
            key={componentName}
            isStickyBar={isStickyBar}
            scrollBackToMainBar={scrollBackToMainBar}
            {...analyticsProps}
          />
        );
      default:
        return null;
    }
  };

  const renderFilters = useCallback(
    (isStickyBar = false) => {
      const filters = getFiltersForPage(slug, Boolean(initialLocation));

      if (showFilterSkeleton) {
        return Array.from({ length: filters.length + (sortOptions.length > 0 ? 1 : 0) }).map(
          (_, i) => <div key={i} className={skeletonClass} />,
        );
      }

      return (
        <>
          {filters.map(({ component }) => renderFilter(component, isStickyBar))}
          {sortOptions.length > 0 && (
            <SortByDropdown
              sortOptions={sortOptions}
              useStore={useOffersFilterStore}
              filterKey="Sort By"
              sortBySelectEvent={sortBySelectEvent}
              {...(isStickyBar && { isStickyBar, scrollBackToMainBar })}
            />
          )}
        </>
      );
    },
    [
      showFilterSkeleton,
      slug,
      initialLocation,
      sortOptions,
      scrollBackToMainBar,
      sortBySelectEvent,
    ],
  );

  return (
    <div className={wrapperClass} ref={filterBoxRef}>
      <div className={filtersRowClass}>{renderFilters()}</div>
      <SelectedFilterPills
        isMobile={false}
        analyticsProps={analyticsProps}
        onClearFilters={onClearFilters}
        onClearFiltersEvent={clearAllFiltersEvent}
        onRemoveFilterEvent={removeFilterEvent}
      />
      {showStickyBar && (
        <StickyBar
          showStickyBar={showStickyBar}
          useStore={useOffersFilterStore}
          scrollBackToMainBar={scrollBackToMainBar}
          onClearFilters={onClearFilters}
          clearAllFiltersEvent={clearAllFiltersEvent}
          initialClientRegion={initialClientRegion}
        >
          {renderFilters(true)}
        </StickyBar>
      )}
    </div>
  );
};
